/**
 * OsCountModeBanner — MADE-292
 *
 * Sticky amber AppBar rendered at the top of every OS page when a count session
 * is active in localStorage. Polls GET /api/os/count-sessions/:sessionId/progress
 * every 30 seconds to keep the count current (instant feedback on the same
 * device comes from useCountMode.updateCountedItems after each scan).
 *
 * Exit opens a bottom-sheet Drawer with two options:
 *   Pause  — clears localStorage, navigates to /os/scan.
 *   Abandon — same. No API call; the server session stays open for the manager.
 *
 * Note: there is intentionally no "Finish" option here — completion is done by
 * the manager in the audit dashboard (MADE-293).
 *
 * Rendered inside OsSessionGuard so it covers all PIN-gated pages automatically.
 * Also rendered directly inside OsCountModeReady (a public route that skips the guard).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  LinearProgress,
  Toolbar,
  Typography,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useCountMode } from '../hooks/useCountMode';
import { useTenant } from '../../contexts/TenantContext';

export const OsCountModeBanner: React.FC = () => {
  const { getCountMode, clearCountMode, updateCountedItems } = useCountMode();
  const { apiBase } = useTenant();
  const navigate = useNavigate();
  const [exitOpen, setExitOpen] = useState(false);

  // Poll count progress every 30 s
  const pollProgress = useCallback(async (sessionId: string) => {
    try {
      const res = await fetch(
        `${apiBase}/count-sessions/${encodeURIComponent(sessionId)}/progress`,
      );
      if (res.ok) {
        const data = await res.json();
        updateCountedItems(data.countedItems, data.totalItems, sessionId, data.rosterMode);
      }
    } catch {
      // ignore poll errors — next interval will retry
    }
  }, [apiBase, updateCountedItems]);

  const countMode = getCountMode();

  useEffect(() => {
    if (!countMode?.sessionId) return;
    const id = setInterval(() => pollProgress(countMode.sessionId), 30_000);
    return () => clearInterval(id);
  }, [countMode?.sessionId, pollProgress]);

  const current = getCountMode();
  if (!current) return null;

  const { sessionName, countedItems, totalItems } = current;
  const pct = totalItems > 0 ? Math.round((countedItems / totalItems) * 100) : 0;

  const handlePause = () => {
    clearCountMode();
    setExitOpen(false);
    navigate('/os/scan');
  };

  // Abandon: same client action (clear state, navigate away).
  // No API call — manager sees the partial session in the audit dashboard.
  const handleAbandon = () => {
    clearCountMode();
    setExitOpen(false);
    navigate('/os/scan');
  };

  return (
    <>
      <AppBar
        position="sticky"
        data-testid="count-mode-banner"
        sx={{ background: '#F57C00', color: '#fff', zIndex: 1300 }}
      >
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <InventoryIcon fontSize="small" />
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ flexGrow: 1 }}
            data-testid="banner-progress-label"
          >
            {sessionName} · {countedItems} / {totalItems}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              width: 80,
              mr: 1,
              bgcolor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': { bgcolor: '#fff' },
            }}
            aria-label={`Count progress: ${pct}%`}
          />
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}
            onClick={() => setExitOpen(true)}
            data-testid="exit-count-mode"
          >
            Exit
          </Button>
        </Toolbar>
      </AppBar>

      {/* Exit bottom sheet */}
      <Drawer
        anchor="bottom"
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px 16px 0 0', p: 3, maxWidth: 600, mx: 'auto' },
        }}
        data-testid="exit-drawer"
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Exit Count Mode
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {countedItems} of {totalItems} items counted so far. Your progress is
          saved — you can resume by scanning the count sheet QR again.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handlePause}
            sx={{
              bgcolor: '#F57C00',
              '&:hover': { bgcolor: '#E65100' },
              py: 1.5,
              fontWeight: 700,
            }}
            data-testid="pause-count-mode"
          >
            Pause — I'll come back later
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleAbandon}
            sx={{
              borderColor: '#F57C00',
              color: '#E65100',
              py: 1.5,
              fontWeight: 700,
            }}
            data-testid="abandon-count-mode"
          >
            Abandon this device's session
          </Button>

          <Button
            fullWidth
            variant="text"
            size="large"
            onClick={() => setExitOpen(false)}
            sx={{ color: 'text.secondary', py: 1.5 }}
            data-testid="cancel-exit"
          >
            Cancel
          </Button>
        </Box>
      </Drawer>
    </>
  );
};
